// Dashboard filters APIs for Project Development

import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import {
  fetchAllDashboardFilters,
  saveDashboardFilters,
  extractDashboardFilters,
} from '../../../../containers/utils/filterUtils';

// Build API base URL from localStorage
const getApiBaseUrl = () => {
  const appName = localStorage.getItem("appName");
  const network = localStorage.getItem("network");
  const collabType = localStorage.getItem("collabType");
  const roleName = localStorage.getItem("roleName");

  if (appName && network && collabType && roleName) {
    return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
  }
  return null;
};

// ===== API FUNCTIONS =====

export const fetchCMOSitesAPI = async () => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) {
      return {
        status: false,
        data: [],
      };
    }

    // Real middleware API (per contract):
    // GET /fetchAllCMOSites
    // Response:
    // {
    //   status: true,
    //   data: {
    //     cmoSitesList: [{ vendorMosymphonyID, cmoSite }]
    //   }
    // }
    const response = await axios.get(`${apiBaseUrl}/fetchAllCMOSites`);

    if (response?.data?.status === true || response?.data?.status === "true") {
      const cmoSitesList = response?.data?.data?.cmoSitesList || [];

      // Return full objects with IDs for ID mapping, but also provide text for display.
      // Contract each item has: { vendorMosymphonyID, cmoSite }
      // (legacy siteNameFromReport tolerated as a fallback).
      const getSiteName = (item) => item?.cmoSite || item?.siteNameFromReport || "";
      return {
        status: true,
        data: cmoSitesList, // Return full objects
        displayData: cmoSitesList.map((item) => {
          // Map to simple string list for dropdown display
          const name = getSiteName(item);
          if (name && name.trim() !== "") {
            return name;
          }
          if (item?.vendorMosymphonyID !== undefined && item.vendorMosymphonyID !== null) {
            return String(item.vendorMosymphonyID);
          }
          return null;
        }).filter(Boolean),
      };
    }

    return {
      status: false,
      data: [],
    };
  } catch (error) {
    return {
      status: false,
      data: [],
    };
  }
};

// Fetch CMO members — the CMO filter's option source (same source the
// Purchase Order Dashboard uses). POST /fetchCMOMembers returns
// CMOMembersWithId: [{ organization_id, organization_name }].
//   organization_id   → the CMO id (equals a plant's vendorMosymphonyID);
//                        sent as `cdmoOrgID` and used to narrow the Site list.
//   organization_name → dropdown label.
export const fetchCMOMembersAPI = async () => {
  try {
    const { appName, network, roleName, collabType, organizationName, ecosystemId } = localStorage;
    if (!appName || !network || !roleName) {
      return { status: false, data: [] };
    }
    // A CMO user has no CMO members to list (mirrors the PO dashboard guard).
    if (collabType === 'CMO') {
      return { status: false, data: [] };
    }

    const url = `${appUrl}/${appName}/${network}/Pharma Co/${roleName}/fetchCMOMembers`;
    const response = await axios.post(url, {
      collabType,
      roleName,
      organizationName,
      ecosystemId,
    });

    if (response?.data?.status === true || response?.data?.status === "true") {
      const members = Array.isArray(response?.data?.CMOMembersWithId)
        ? response.data.CMOMembersWithId
        : [];
      return {
        status: true,
        data: members,
      };
    }

    return {
      status: false,
      data: [],
    };
  } catch (error) {
    return {
      status: false,
      data: [],
    };
  }
};

// Fetch manufacturing plants — the Site filter's option source.
// GET /fetchMfgPlants
// Response: { status, MfgPlantsDetails: [{ aslNumber, mfgPlantShortName, vendorMosymphonyID, SiteName }] }
//   SiteName → the parent CMO site's name (dropdown label + payload `cmoSite`);
//     mfgPlantShortName is kept as a fallback for rows without a linked site.
//   vendorMosymphonyID → the CMO the plant belongs to (used to narrow the Site
//     list to the selected CMO, and sent as `cdmoOrgID`).
export const fetchMfgPlantsAPI = async () => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) {
      return {
        status: false,
        data: [],
      };
    }

    const response = await axios.get(`${apiBaseUrl}/fetchMfgPlants`);

    if (response?.data?.status === true || response?.data?.status === "true") {
      const plants = Array.isArray(response?.data?.MfgPlantsDetails)
        ? response.data.MfgPlantsDetails
        : [];
      return {
        status: true,
        data: plants,
      };
    }

    return {
      status: false,
      data: [],
    };
  } catch (error) {
    return {
      status: false,
      data: [],
    };
  }
};

export const fetchDeliverableTypesAPI = async () => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) {
      return {
        status: false,
        data: [],
      };
    }

    // Call the real API endpoint with projectConfig=true
    const response = await axios.get(`${apiBaseUrl}/fetchDeliverableTypes/true`);

    if (response?.data?.status === true || response?.data?.status === "true") {
      // Extract deliverableTypeName from each item in deliverablesTypes array
      const deliverablesTypes = response?.data?.data?.deliverablesTypes || [];
      
      // Filter only activated items and return full objects with IDs
      const activatedTypes = deliverablesTypes.filter(item => item.activated === true);
      
      // Also provide display data (just names) for backward compatibility
      const deliverableTypeNames = activatedTypes.map(item => item.deliverableTypeName);

      return {
        status: true,
        data: activatedTypes, // Return full objects with deliverableTypeID
        displayData: deliverableTypeNames, // Display names for dropdown
      };
    }

    return {
      status: false,
      data: [],
    };
  } catch (error) {
    return {
      status: false,
      data: [],
    };
  }
};

// Fetch data granularity levels for Add New Project
export const fetchDataGranularityLevelsAPI = async () => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) {
      return {
        status: false,
        data: [],
      };
    }

    // Append /true as required by backend (e.g., for projectConfig context)
    const response = await axios.get(`${apiBaseUrl}/fetchDataGranularityLevels/true`);

    if (response?.data?.status === true || response?.data?.status === "true") {
      const levels = response?.data?.data?.dataGranularityLevels || [];

      // Only keep activated levels
      const activatedLevels = levels.filter(level => level.activated === true);

      return {
        status: true,
        data: activatedLevels, // full objects with IDs
        displayData: activatedLevels.map(level => level.dataGranularityLevelName),
      };
    }

    return {
      status: false,
      data: [],
    };
  } catch (error) {
    return {
      status: false,
      data: [],
    };
  }
};

// Saved dashboard filters use the shared, cross-dashboard endpoints
// (GET /user/fetchAllFilters, POST /user/saveFilters) via containers/utils/
// filterUtils.js - the same pair the Purchase Order dashboard uses. All
// dashboards share one stored `save_filters` document keyed by dashboard, and
// a save posts ONLY this dashboard's key, so the backend merges it in and the
// other dashboards' saved filters are left untouched.
export const PD_DASHBOARD_FILTER_KEY = 'projectDevelopmentDashboard';

// Loads this dashboard's saved filter selection.
// extractDashboardFilters already returns { status, save_filter_options },
// which is the shape useProductDevelopment expects. When the user has never
// saved filters the key is absent and this returns { status: false }, so the
// caller falls back to its defaults.
export const fetchAllFiltersAPI = async () => {
  const allFilters = await fetchAllDashboardFilters();
  return extractDashboardFilters(allFilters, PD_DASHBOARD_FILTER_KEY);
};

// Persists this dashboard's filter selection.
// Returns the API response, which carries { status } (and may carry a message).
export const saveFiltersAPI = async (filters = {}) => {
  return saveDashboardFilters(PD_DASHBOARD_FILTER_KEY, filters);
};


